import { useMutation, useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import Toaster from 'components/Toaster';
import {
  PageShell,
  PageHeader,
  DataTable,
  SearchInput,
  AddButton,
  SegmentedFilter,
  ActionIcon,
  StatusPill,
  EntityCell,
  ConfirmModal,
  tokens,
} from 'components/v3';
import { query as GET_TRACKS_ADMIN } from './gql/queries/get-tracks';
import TrackDetailModal from './TrackDetailModal';

// ============================================================
// Gen-3 Tracks — composed from v3 components, wired to getTracksAdmin.
// ============================================================

const LIMIT = 15;

const DELETE_TRACK = gql`
  mutation DeleteTrack($id: Int!) {
    deleteTrack(id: $id)
  }
`;

// Long IANA zone -> short label.
const TZ_SHORT = {
  'America/New_York': 'Eastern (ET)',
  'America/Chicago': 'Central (CT)',
  'America/Denver': 'Mountain (MT)',
  'America/Los_Angeles': 'Pacific (PT)',
  'America/Phoenix': 'Arizona (MST)',
};

// SegmentedFilter key -> backend TracksAdminFilter enum.
const FILTER_ENUM = { live: 'LIVE', active: 'ACTIVE', inactive: 'INACTIVE' };

const COLUMNS = [
  {
    key: 'track',
    label: 'Track',
    width: 'minmax(300px,1.8fr)',
    primary: true,
    skeleton: 'media',
  },
  { key: 'type', label: 'Track Type', width: 'minmax(130px,1fr)' },
  { key: 'events', label: 'Events', width: '84px' },
  { key: 'timezone', label: 'Time Zone', width: '140px' },
  { key: 'status', label: 'Status', width: '120px', skeleton: 'pill' },
  {
    key: 'actions',
    label: 'Actions',
    width: '124px',
    align: 'right',
    skeleton: 'actions',
    actionsCount: 4,
  },
];

// Status badge: LIVE when there are published events this year, else
// Active/Inactive per the track's `active` flag.
function renderStatus(track) {
  if (track.eventsCount > 0) {
    return (
      <StatusPill
        label="Live"
        color="#1F9D57"
        background="rgba(52,210,123,0.13)"
        border="rgba(52,210,123,0.34)"
      />
    );
  }
  if (track.active === false) {
    return (
      <StatusPill
        label="Inactive"
        color="#64748b"
        background="rgba(100,116,139,0.13)"
        border="rgba(100,116,139,0.34)"
      />
    );
  }
  return (
    <StatusPill
      label="Active"
      color="#2C6FD6"
      background="rgba(44,111,214,0.13)"
      border="rgba(44,111,214,0.34)"
    />
  );
}

const Tracks = () => {
  const t = tokens(useTheme());
  const history = useHistory();
  const [filter, setFilter] = useState('live');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [confirmTrack, setConfirmTrack] = useState(null);
  const [viewTrack, setViewTrack] = useState(null);
  const [toasterData, setToasterData] = useState(null);

  const input = {
    filter: FILTER_ENUM[filter],
    page: String(page),
    limit: String(LIMIT),
  };
  if (search) input.queryString = search;

  const { data, loading, refetch } = useQuery(GET_TRACKS_ADMIN, {
    variables: { input },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [deleteTrack, { loading: deleting }] = useMutation(DELETE_TRACK);

  const result = data && data.getTracksAdmin;
  const tracks = (result && result.results) || [];
  const counts = (result && result.counts) || {
    live: 0,
    active: 0,
    inactive: 0,
  };
  const total = (result && result.count) || 0;
  const pageCount = Math.max(1, Math.ceil(total / LIMIT));

  const onFilter = (key) => {
    setFilter(key);
    setPage(1);
  };
  const onSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleDelete = async () => {
    const track = confirmTrack;
    if (!track) return;
    try {
      const res = await deleteTrack({
        variables: { id: parseInt(track.id, 10) },
      });
      if (res && res.data && res.data.deleteTrack) {
        setToasterData({
          title: 'Track deleted',
          message: `${track.name} has been deleted.`,
          type: 'success',
        });
        await refetch();
      } else {
        throw new Error('delete failed');
      }
    } catch (e) {
      setToasterData({
        title: 'Delete failed',
        message: `We couldn't delete ${track.name}. Please try again.`,
        type: 'error',
      });
    } finally {
      setConfirmTrack(null);
    }
  };

  const renderRow = (tr) => ({
    track: (
      <EntityCell
        logo={tr.logo}
        code={tr.short_name}
        title={tr.name}
        subtitle={tr.cityAndState}
      />
    ),
    type: (tr.type && (tr.type.key || tr.type.value)) || '',
    events: (
      <span
        style={{
          fontSize: '14.5px',
          fontWeight: 600,
          color: tr.eventsCount ? t.text : t.faint,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {tr.eventsCount}
      </span>
    ),
    timezone: TZ_SHORT[tr.timezone] || tr.timezone || '',
    status: renderStatus(tr),
    actions: (
      <div style={{ display: 'flex', gap: 3 }}>
        <ActionIcon icon="eye" title="View" onClick={() => setViewTrack(tr)} />
        <ActionIcon
          icon="calendar"
          title="Events"
          to={`/admin/track/events/${tr.id}`}
        />
        <ActionIcon
          icon="square-pen"
          title="Edit"
          to={`/admin/tracks/editTrack/${tr.id}`}
        />
        <ActionIcon
          icon="trash-2"
          title="Delete"
          onClick={() => setConfirmTrack(tr)}
        />
      </div>
    ),
  });

  return (
    <PageShell>
      <PageHeader icon="flag" title="Tracks" />

      <DataTable
        columns={COLUMNS}
        data={tracks}
        renderRow={renderRow}
        loading={loading}
        emptyLabel="No tracks in this view."
        page={page}
        pageCount={pageCount}
        onPageChange={setPage}
        toolbar={
          <>
            <SearchInput
              onChange={onSearch}
              placeholder="Search tracks..."
              debounce={350}
            />
            <AddButton title="Add Track" to="/admin/tracks/addTrack" />
          </>
        }
        filter={
          <SegmentedFilter
            value={filter}
            onChange={onFilter}
            options={[
              {
                key: 'live',
                label: 'Live Events',
                count: counts.live,
                dot: true,
                dotColor: '#1F9D57',
              },
              { key: 'active', label: 'Active', count: counts.active },
              { key: 'inactive', label: 'Inactive', count: counts.inactive },
            ]}
          />
        }
      />

      <TrackDetailModal
        track={viewTrack}
        statusNode={viewTrack ? renderStatus(viewTrack) : null}
        tzLabel={
          viewTrack
            ? TZ_SHORT[viewTrack.timezone] || viewTrack.timezone || ''
            : ''
        }
        onEdit={() =>
          viewTrack && history.push(`/admin/tracks/editTrack/${viewTrack.id}`)
        }
        onClose={() => setViewTrack(null)}
      />

      <ConfirmModal
        open={!!confirmTrack}
        title="Delete this track?"
        message={
          confirmTrack
            ? `${confirmTrack.name} will be permanently deleted. This can't be undone.`
            : ''
        }
        confirmLabel="Delete"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmTrack(null)}
      />

      {toasterData && (
        <Toaster
          isOpen
          title={toasterData.title}
          message={toasterData.message}
          type={toasterData.type}
          onClose={() => setToasterData(null)}
        />
      )}
    </PageShell>
  );
};

export default Tracks;
